import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber, Form } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';
import RichEditor from '../../components/RichEditor';
import UniImage from '../../components/UniImage';

const VipCards = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [showPublishCntModal, setShowPublishCntModal] = useState(false);
    const [publishCntRecord, setPublishCntRecord] = useState(null);
    const [publishCnt, setPublishCnt] = useState('');
    const [maxCnt, setMaxCnt] = useState('');
    const [updatePublishCntLoading, setUpdatePublishCntLoading] = useState(false);

    const statusMap = {
        0: { text: '正常', type: 'success' },
        1: { text: '隐藏', type: 'warning' },
    };

    const openPublishCntModal = (record) => {
        setPublishCntRecord(record);
        setPublishCnt((record?.publish_cnt ?? '').toString());
        setMaxCnt((record?.max_cnt ?? '').toString());
        setShowPublishCntModal(true);
    };

    const handleUpdatePublishCnt = async () => {
        if (!publishCntRecord) return;
        setUpdatePublishCntLoading(true);
        try {
            const response = await request.post('/go/sp/vip/edit_vip_card/', {
                uid: publishCntRecord.uid,
                publish_cnt: publishCnt.toString(),
                max_cnt: maxCnt.toString()
            });
            if (response.errno === 0) {
                Toast.success('更新成功');
                setShowPublishCntModal(false);
                fetchList(pagination.currentPage, pagination.pageSize);
            } else {
                Toast.error(response.errstr || '更新失败');
            }
        } catch (error) {
            console.error('更新失败:', error);
            Toast.error('更新失败');
        } finally {
            setUpdatePublishCntLoading(false);
        }
    };

    const typeMap = {
        1: '储值卡',
        2: '次卡',
        4: '折扣卡'
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/vip/list_vip_card/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/vip/add_vip_card/', {
                ...values,
                price: Math.round(values.price * 100), // 转换为分
                rule: JSON.stringify({
                    ...values.rule,
                    charge_cash: Math.round(values.rule.charge_cash * 100), // 转换为分
                    duration: values.rule.duration * 24 * 60 * 60 // 转换为秒
                })
            });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/vip/edit_vip_card/', {
                ...values,
                uid: currentRecord.uid,
                price: Math.round(values.price * 100), // 转换为分
                rule: JSON.stringify({
                    ...values.rule,
                    charge_cash: Math.round(values.rule.charge_cash * 100), // 转换为分
                    duration: values.rule.duration * 24 * 60 * 60 // 转换为秒
                })
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/vip/delete_vip_card/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的会员卡');
            return;
        }
        try {
            const response = await request.post('/go/sp/vip/batchedit_vip_card/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const parseRule = (ruleStr) => {
        try {
            return typeof ruleStr === 'string' ? JSON.parse(ruleStr) : ruleStr;
        } catch (error) {
            return {};
        }
    };

    const columns = [
        {
            title: '会员卡信息',
            dataIndex: 'name',
            width: 300,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {record.image && <UniImage src={record.image} width={40} height={40} style={{ borderRadius: 4 }} alt={text} />}
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>价格: {(record.price / 100).toFixed(2)}元</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>类型: {typeMap[record.typ] || '-'}</div>
                    </div>
                </div>
            )
        },
        {
            title: '会员权益',
            dataIndex: 'rule',
            width: 250,
            render: (ruleStr) => {
                const rule = parseRule(ruleStr);
                return (
                    <div style={{ fontSize: '12px' }}>
                        {rule.discount_rate && rule.discount_rate < 100 && <div>折扣: {rule.discount_rate}%</div>}
                        {rule.charge_cash > 0 && <div>赠送余额: {(rule.charge_cash / 100).toFixed(2)}元</div>}
                        {rule.charge_cnt > 0 && <div>赠送服务次数: {rule.charge_cnt}次</div>}
                        {rule.duration > 0 && <div>有效期: {Math.floor(rule.duration / (24 * 60 * 60))}天</div>}
                    </div>
                );
            }
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '数量',
            dataIndex: 'publish_cnt',
            width: 150,
            render: (text, record) => (
                <Button theme='borderless' onClick={() => openPublishCntModal(record)}>{`${text}/${record.max_cnt}`}</Button>
            )
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100,
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            const rule = parseRule(record.rule);
                            setCurrentRecord({
                                ...record,
                                price: record.price / 100,
                                rule: {
                                    ...rule,
                                    charge_cash: rule.charge_cash ? parseFloat((rule.charge_cash / 100).toFixed(2)) : 0,
                                    duration: rule.duration ? Math.floor(rule.duration / (24 * 60 * 60)) : 0
                                }
                            });
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该会员卡吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            name: currentRecord.name,
            image: currentRecord.image,
            status: currentRecord.status,
            sort: currentRecord.sort,
            max_cnt: currentRecord.max_cnt,
            publish_cnt: currentRecord.publish_cnt,
            price: currentRecord.price,
            typ: currentRecord.typ || 1,
            rule: {
                ...parseRule(currentRecord.rule),
                html: parseRule(currentRecord.rule)?.html || '',
                charge_cash: parseRule(currentRecord.rule)?.charge_cash || 0,
                duration: parseRule(currentRecord.rule)?.duration || 0,
            }
        } : {
            name: '',
            image: '',
            status: 0,
            sort: 0,
            max_cnt: '',
            publish_cnt: '0',
            price: 0,
            typ: 1,
            rule: {
                html: '',
                discount_rate: 100,
                charge_cash: 0,
                charge_cnt: 0,
                duration: 0
            }
        };

        console.log("iiiii", type, initialValues, currentRecord);
        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    if (isEdit) {
                        handleEdit(values);
                    } else {
                        handleAdd(values);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Input
                    field="name"
                    label="会员卡名称"
                    rules={[{ required: true, message: '请输入会员卡名称' }]}
                />
                <Form.Slot label="会员卡图标">
                    <ImageSelector
                        enableTDesignIcon={true}
                        value={formApi?.getValue('image')}
                        onChange={value => formApi?.setValue('image', value)}
                        placeholder="请输入会员卡图标URL"
                        width="100%"
                        previewSize={100}
                    />
                </Form.Slot>
                <Form.Select
                    field="typ"
                    label="会员卡类型"
                    rules={[{ required: true, message: '请选择会员卡类型' }]}
                >
                    {Object.entries(typeMap).map(([value, text]) => (
                        <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                    ))}
                </Form.Select>
                <Form.InputNumber
                    field="price"
                    label="开卡价格(元)"
                    min={0}
                    precision={2}
                />
                <Form.Input
                    field="max_cnt"
                    label="总数量"
                    rules={[{ required: true, message: '请输入总数量' }]}
                    placeholder="请输入总数量"
                />
                <Form.InputNumber
                    field="sort"
                    label="排序"
                    min={0}
                />
                <Form.Select
                    field="status"
                    label="状态"
                >
                    <Select.Option value={0}>正常</Select.Option>
                    <Select.Option value={1}>隐藏</Select.Option>
                </Form.Select>
                <Form.InputNumber
                    field="rule.discount_rate"
                    label="折扣率(%)"
                    min={0}
                    max={100}
                />
                <Form.InputNumber
                    field="rule.charge_cash"
                    label="开卡赠送余额(元)"
                    min={0}
                    precision={2}
                />
                <Form.InputNumber
                    field="rule.charge_cnt"
                    label="开卡赠送服务次数"
                    min={0}
                />
                <Form.InputNumber
                    field="rule.duration"
                    label="有效期(天)"
                    min={0}
                />
                <Form.Slot label="会员卡介绍">
                    <RichEditor
                        value={formApi?.getValue('rule.html') || ''}
                        onChange={value => formApi?.setValue('rule.html', value)}
                        style={{ height: 300 }}
                    />
                </Form.Slot>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.vipCardsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索会员卡名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="会员卡状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增会员卡</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量隐藏</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量显示</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个会员卡吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增会员卡"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑会员卡"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>

            <Modal
                title="修改已开卡数量"
                visible={showPublishCntModal}
                onCancel={() => setShowPublishCntModal(false)}
                footer={null}
                centered
                width={420}
            >
                <div style={{ padding: '12px 0' }}>
                    <Space vertical align="start" style={{ width: '100%' }}>
                        <div style={{ width: '100%' }}>
                            <div style={{ fontSize: 12, color: 'var(--semi-color-text-2)', marginBottom: 4 }}>已开卡数量</div>
                            <Input
                                value={publishCnt}
                                onChange={setPublishCnt}
                                showClear
                                placeholder="请输入已开卡数量（字符串）"
                            />
                        </div>
                        <div style={{ width: '100%' }}>
                            <div style={{ fontSize: 12, color: 'var(--semi-color-text-2)', marginBottom: 4 }}>总数量</div>
                            <Input
                                value={maxCnt}
                                onChange={setMaxCnt}
                                showClear
                                placeholder="请输入总数量（字符串）"
                            />
                        </div>
                    </Space>
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setShowPublishCntModal(false)}>取消</Button>
                            <Button type='primary' loading={updatePublishCntLoading} onClick={handleUpdatePublishCnt}>确定</Button>
                        </Space>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default VipCards;